import { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { FormattedMessage, injectIntl } from "react-intl";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { saveTeamState } from "../../_redux/teamsSlice";
import { actions } from "../../../Auth/_redux/authRedux";

const TeamEditModal = (props) => {
  const dispatch = useDispatch();
  const { intl, handlePublishProposal } = props;
  const [loading, setLoading] = useState(false);

  let initialValues = {
    name: "",
    fieldOfOperations: "",
    regionOfOperations: "",
    isNgo: false,
    foundationYear: null,
    quantityOfMembers: null,
    quantityOfInstitutionalMembers: null,
    quantityOfEmployees: null,
    about: "",
    website: "",
    email: "",
    contactNumber: "",
  };

  if (props.team) {
    initialValues = {
      name: props.team.name ?? "",
      fieldOfOperations: props.team.fieldOfOperations ?? "",
      regionOfOperations: props.team.regionOfOperations ?? "",
      isNgo: props.team.isNgo ?? false,
      foundationYear: props.team.foundationYear,
      quantityOfMembers: props.team.quantityOfMembers,
      quantityOfInstitutionalMembers: props.team.quantityOfInstitutionalMembers,
      quantityOfEmployees: props.team.quantityOfEmployees,
      about: props.team.about ?? "",
      website: props.team.website ?? "",
      email: props.team.email ?? "",
      contactNumber: props.team.contactNumber ?? "",
    };
  }

  const TeamEditSchema = Yup.object().shape({
    name: Yup.string(),
    fieldOfOperations: Yup.string()
      .min(
        2,
        intl.formatMessage({ id: "AUTH.VALIDATION.MIN_LENGTH" }, { length: 2 })
      )
      .max(
        50,
        intl.formatMessage({ id: "AUTH.VALIDATION.MAX_LENGTH" }, { length: 50 })
      )
      .required(
        intl.formatMessage(
          { id: "AUTH.VALIDATION.REQUIRED" },
          {
            name: intl.formatMessage({
              id: "TEAM.EDIT.LABEL.FIELD_OF_OPERATIONS",
            }),
          }
        )
      ),
    regionOfOperations: Yup.string()
      .min(
        2,
        intl.formatMessage({ id: "AUTH.VALIDATION.MIN_LENGTH" }, { length: 2 })
      )
      .max(
        255,
        intl.formatMessage({ id: "AUTH.VALIDATION.MAX_LENGTH" }, { length: 50 })
      )
      .required(
        intl.formatMessage(
          { id: "AUTH.VALIDATION.REQUIRED" },
          {
            name: intl.formatMessage({
              id: "TEAM.EDIT.LABEL.REGION_OF_OPERATIONS",
            }),
          }
        )
      ),
    isNgo: Yup.boolean(),
    foundationYear: Yup.number()
      .typeError(intl.formatMessage({ id: "VALIDATION.NUMBER" }))
      .test(
        "len",
        intl.formatMessage({ id: "VALIDATION.LENGTH" }, { length: 4 }),
        (val) => val.toString().length === 4
      )
      .required(
        intl.formatMessage(
          { id: "AUTH.VALIDATION.REQUIRED" },
          {
            name: intl.formatMessage({
              id: "TEAM.EDIT.LABEL.FOUNDATION_YEAR",
            }),
          }
        )
      ),
    quantityOfMembers: Yup.number()
      .typeError(intl.formatMessage({ id: "VALIDATION.NUMBER" }))
      .required(
        intl.formatMessage(
          { id: "AUTH.VALIDATION.REQUIRED" },
          {
            name: intl.formatMessage({
              id: "TEAM.EDIT.LABEL.QUANTITY_OF_MEMBERS",
            }),
          }
        )
      ),
    quantityOfInstitutionalMembers: Yup.number()
      .typeError(intl.formatMessage({ id: "VALIDATION.NUMBER" }))
      .required(
        intl.formatMessage(
          { id: "AUTH.VALIDATION.REQUIRED" },
          {
            name: intl.formatMessage({
              id: "TEAM.EDIT.LABEL.QUANTITY_OF_INSTITUTIONAL_MEMBERS",
            }),
          }
        )
      ),
    quantityOfEmployees: Yup.number()
      .typeError(intl.formatMessage({ id: "VALIDATION.NUMBER" }))
      .required(
        intl.formatMessage(
          { id: "AUTH.VALIDATION.REQUIRED" },
          {
            name: intl.formatMessage({
              id: "TEAM.EDIT.LABEL.QUANTITY_OF_EMPLOYEES",
            }),
          }
        )
      ),
    about: Yup.string()
      .min(
        3,
        intl.formatMessage({ id: "AUTH.VALIDATION.MIN_LENGTH" }, { length: 3 })
      )
      .max(
        255,
        intl.formatMessage({ id: "AUTH.VALIDATION.MAX_LENGTH" }, { length: 50 })
      )
      .required(
        intl.formatMessage(
          { id: "AUTH.VALIDATION.REQUIRED" },
          {
            name: intl.formatMessage({
              id: "TEAM.EDIT.LABEL.ABOUT",
            }),
          }
        )
      ),
    website: Yup.string().matches(
      /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      intl.formatMessage({
        id: "VALIDATION.URL",
      })
    ),
    email: Yup.string()
      .email(
        intl.formatMessage(
          { id: "AUTH.VALIDATION.WRONG_FORMAT" },
          {
            name: intl.formatMessage({
              id: "TEAM.EDIT.LABEL.EMAIL",
            }),
          }
        )
      )
      .required(
        intl.formatMessage(
          { id: "AUTH.VALIDATION.REQUIRED" },
          {
            name: intl.formatMessage({
              id: "TEAM.EDIT.LABEL.EMAIL",
            }),
          }
        )
      ),
    contactNumber: Yup.string()
      .min(
        3,
        intl.formatMessage({ id: "AUTH.VALIDATION.MIN_LENGTH" }, { length: 3 })
      )
      .max(
        50,
        intl.formatMessage({ id: "AUTH.VALIDATION.MAX_LENGTH" }, { length: 50 })
      )
      .required(
        intl.formatMessage(
          { id: "AUTH.VALIDATION.REQUIRED" },
          {
            name: intl.formatMessage({
              id: "TEAM.EDIT.LABEL.CONTACT_NUMBER",
            }),
          }
        )
      ),
  });

  const enableLoading = () => {
    setLoading(true);
  };

  const disableLoading = () => {
    setLoading(false);
  };

  const formik = useFormik({
    initialValues,
    validationSchema: TeamEditSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setSubmitting(true);
      enableLoading();
      const teamId = localStorage.getItem("teamId");
      const {
        name,
        foundationYear,
        quantityOfMembers,
        quantityOfInstitutionalMembers,
        quantityOfEmployees,
        ...payload
      } = values;
      await dispatch(
        saveTeamState(+teamId, {
          ...payload,
          foundationYear: +foundationYear,
          quantityOfMembers: +quantityOfMembers,
          quantityOfInstitutionalMembers: +quantityOfInstitutionalMembers,
          quantityOfEmployees: +quantityOfEmployees,
        })
      );
      dispatch(actions.requestUser());
      setTimeout(() => {
        disableLoading();
        setSubmitting(false);
        if (handlePublishProposal !== undefined) {
          handlePublishProposal();
        }
        props.onHide();
      }, 1000);
    },
  });

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return "is-invalid";
    }

    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return "is-valid";
    }

    return "";
  };

  return (
    <Modal
      {...props}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      scrollable={true}
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          <FormattedMessage id="TEAM.EDIT.FORM_TITLE" />
        </Modal.Title>
        <FormattedMessage id="TEAM.EDIT.NOTICE" />
      </Modal.Header>
      <form
        onSubmit={formik.handleSubmit}
        className="form fv-plugins-bootstrap fv-plugins-framework"
      >
        <Modal.Body
          style={{
            maxHeight: "calc(100vh - 210px)",
            overflowY: "auto",
          }}
        >
          <div>
            {formik.status ? (
              <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
                <div className="alert-text font-weight-bold">
                  {intl.formatMessage({
                    id: formik.status,
                    defaultMessage: intl.formatMessage({ id: "GENERIC_ERROR" }),
                  })}
                </div>
              </div>
            ) : (
              <div></div>
            )}

            <div className="form-group fv-plugins-icon-container">
              <label htmlFor="name">
                <FormattedMessage id="TEAM.EDIT.LABEL.NAME" />
              </label>
              <input
                type="text"
                className={"form-control"}
                name="name"
                readOnly={true}
                {...formik.getFieldProps("name")}
              />
            </div>
            <div className="form-group fv-plugins-icon-container">
              <label htmlFor="fieldOfOperations">
                <FormattedMessage id="TEAM.EDIT.LABEL.FIELD_OF_OPERATIONS" />
              </label>
              <input
                type="text"
                className={`form-control ${getInputClasses(
                  "fieldOfOperations"
                )}`}
                name="fieldOfOperations"
                {...formik.getFieldProps("fieldOfOperations")}
              />
              {formik.touched.fieldOfOperations &&
              formik.errors.fieldOfOperations ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    {formik.errors.fieldOfOperations}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="form-group fv-plugins-icon-container">
              <label htmlFor="regionOfOperations">
                <FormattedMessage id="TEAM.EDIT.LABEL.REGION_OF_OPERATIONS" />
              </label>
              <input
                type="text"
                className={`form-control ${getInputClasses(
                  "regionOfOperations"
                )}`}
                name="regionOfOperations"
                {...formik.getFieldProps("regionOfOperations")}
              />
              {formik.touched.regionOfOperations &&
              formik.errors.regionOfOperations ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    {formik.errors.regionOfOperations}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="form-group fv-plugins-icon-container">
              <label htmlFor="isNgo">
                <FormattedMessage id="TEAM.EDIT.LABEL.NGO" />
              </label>
              <input
                type="checkbox"
                className="ml-2"
                name="isNgo"
                checked={formik.getFieldProps("isNgo").value}
                {...formik.getFieldProps("isNgo")}
              />
            </div>
            <div className="form-group fv-plugins-icon-container">
              <label htmlFor="foundationYear">
                <FormattedMessage id="TEAM.EDIT.LABEL.FOUNDATION_YEAR" />
              </label>
              <input
                type="text"
                className={`form-control ${getInputClasses("foundationYear")}`}
                name="foundationYear"
                {...formik.getFieldProps("foundationYear")}
              />
              {formik.touched.foundationYear && formik.errors.foundationYear ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    {formik.errors.foundationYear}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="form-group fv-plugins-icon-container">
              <label htmlFor="quantityOfMembers">
                <FormattedMessage id="TEAM.EDIT.LABEL.QUANTITY_OF_MEMBERS" />
              </label>
              <input
                type="text"
                className={`form-control ${getInputClasses(
                  "quantityOfMembers"
                )}`}
                name="quantityOfMembers"
                {...formik.getFieldProps("quantityOfMembers")}
              />
              {formik.touched.quantityOfMembers &&
              formik.errors.quantityOfMembers ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    {formik.errors.quantityOfMembers}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="form-group fv-plugins-icon-container">
              <label htmlFor="quantityOfInstitutionalMembers">
                <FormattedMessage id="TEAM.EDIT.LABEL.QUANTITY_OF_INSTITUTIONAL_MEMBERS" />
              </label>
              <input
                type="text"
                className={`form-control ${getInputClasses(
                  "quantityOfInstitutionalMembers"
                )}`}
                name="quantityOfInstitutionalMembers"
                {...formik.getFieldProps("quantityOfInstitutionalMembers")}
              />
              {formik.touched.quantityOfInstitutionalMembers &&
              formik.errors.quantityOfInstitutionalMembers ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    {formik.errors.quantityOfInstitutionalMembers}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="form-group fv-plugins-icon-container">
              <label htmlFor="quantityOfEmployees">
                <FormattedMessage id="TEAM.EDIT.LABEL.QUANTITY_OF_EMPLOYEES" />
              </label>
              <input
                type="text"
                className={`form-control ${getInputClasses(
                  "quantityOfEmployees"
                )}`}
                name="quantityOfEmployees"
                {...formik.getFieldProps("quantityOfEmployees")}
              />
              {formik.touched.quantityOfEmployees &&
              formik.errors.quantityOfEmployees ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    {formik.errors.quantityOfEmployees}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="form-group fv-plugins-icon-container">
              <label htmlFor="about">
                <FormattedMessage id="TEAM.EDIT.LABEL.ABOUT" />
              </label>
              <input
                type="text"
                className={`form-control ${getInputClasses("about")}`}
                name="about"
                {...formik.getFieldProps("about")}
              />
              {formik.touched.about && formik.errors.about ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.about}</div>
                </div>
              ) : null}
            </div>
            <div className="form-group fv-plugins-icon-container">
              <label htmlFor="website">
                <FormattedMessage id="TEAM.EDIT.LABEL.WEBSITE" />
              </label>
              <input
                type="text"
                className={`form-control ${getInputClasses("website")}`}
                name="website"
                {...formik.getFieldProps("website")}
              />
              {formik.touched.website && formik.errors.website ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.website}</div>
                </div>
              ) : null}
            </div>
            <div className="form-group fv-plugins-icon-container">
              <label htmlFor="email">
                <FormattedMessage id="TEAM.EDIT.LABEL.EMAIL" />
              </label>
              <input
                type="text"
                className={`form-control ${getInputClasses("email")}`}
                name="email"
                {...formik.getFieldProps("email")}
              />
              {formik.touched.email && formik.errors.email ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.email}</div>
                </div>
              ) : null}
            </div>
            <div className="form-group fv-plugins-icon-container">
              <label htmlFor="contactNumber">
                <FormattedMessage id="TEAM.EDIT.LABEL.CONTACT_NUMBER" />
              </label>
              <input
                type="text"
                className={`form-control ${getInputClasses("contactNumber")}`}
                name="contactNumber"
                {...formik.getFieldProps("contactNumber")}
              />
              {formik.touched.contactNumber && formik.errors.contactNumber ? (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">
                    {formik.errors.contactNumber}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={(e) => {
              e.preventDefault();
              props.onHide();
            }}
          >
            <FormattedMessage id="AUTH.GENERAL.CANCEL_BUTTON" />
          </Button>
          <Button type="submit" disabled={formik.isSubmitting}>
            <FormattedMessage id="BILL.ADD_COMMENT.SAVE_BTN" />
            {loading && <span className="ml-3 spinner spinner-white"></span>}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default injectIntl(TeamEditModal);
